import { PropsWithChildren, useId, useRef, useState } from "react";
import { useFieldContext } from "../../hooks/useAppForm";
import { FieldError } from "../FieldErrors/FieldErrors";
import { ComboBoxProvider } from "./contexts/ComboBoxProvider";

export type ComboBoxProps = PropsWithChildren<{
  errors?: FieldError;
  form?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  value: string;
}>;

export function ComboBox({
  children,
  errors,
  form,
  onChange,
  value,
}: ComboBoxProps) {
  const textFieldRef = useRef<HTMLLabelElement>(null);
  const optionsRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const [open, setOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState(false);
  const [activeDescendantId, setActiveDescendantId] = useState<
    string | undefined
  >(undefined);
  const id = useId();
  const comboBoxId = `combo-box-${id}`;
  const labelId = `combo-box-label-${id}`;

  function onSetOpen(next: boolean) {
    if (!next) {
      setActiveDescendantId(undefined);
      setFilterOptions(false);
    }
    setOpen(next);
  }

  return (
    <ComboBoxProvider
      activeDescendantId={activeDescendantId}
      comboBoxId={comboBoxId}
      errors={errors}
      filterOptions={filterOptions}
      form={form}
      labelId={labelId}
      open={open}
      optionsRefs={optionsRefs}
      setActiveDescendantId={setActiveDescendantId}
      setFilterOptions={setFilterOptions}
      setOpen={onSetOpen}
      setValue={onChange}
      textFieldRef={textFieldRef}
      value={value}
    >
      {children}
    </ComboBoxProvider>
  );
}

export function ComboBoxField(
  props: Omit<ComboBoxProps, "form" | "onChange" | "value">,
) {
  const field = useFieldContext<string>();
  return (
    <ComboBox
      errors={field.state.meta.errors.map((e) =>
        typeof e === "string" ? e : e?.message,
      )}
      {...props}
      form={field.form.formId}
      onBlur={field.handleBlur}
      onChange={field.handleChange}
      value={field.state.value ?? ""}
    />
  );
}
